import { normaliseRoutePath } from "./routePath";

// 菜单数据组装只在此处处理：完整菜单 + 当前角色 ifMap -> 可渲染树。
// 不依赖 Vue、路由或页面组件，方便五个企业端保持同一份逻辑。
const MAX_SORT_VALUE = 9007199254740991;

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function toKey(value) {
  if (typeof value !== "number" && typeof value !== "string") {
    return "";
  }

  const key = String(value).trim();
  return key ? key : "";
}

function getParentKey(pid) {
  if (pid === 0 || pid === "0" || pid === null || pid === undefined) {
    return "";
  }

  return toKey(pid);
}

function getSortValue(value) {
  const numberValue = Number(value);
  return isFinite(numberValue) ? numberValue : MAX_SORT_VALUE;
}

function sortNodes(nodes) {
  return nodes.sort(function(first, second) {
    const sortDifference =
      getSortValue(first.sort) - getSortValue(second.sort);

    if (sortDifference) {
      return sortDifference;
    }

    return String(first.menuId).localeCompare(String(second.menuId));
  });
}

function indexMenus(menuList, diagnostics) {
  const menuById = Object.create(null);

  (Array.isArray(menuList) ? menuList : []).forEach(function(menu) {
    if (!menu || typeof menu !== "object") {
      diagnostics.invalidMenus += 1;
      return;
    }

    const menuId = toKey(menu.menuId);
    if (!menuId) {
      diagnostics.invalidMenus += 1;
      return;
    }

    if (hasOwn(menuById, menuId)) {
      diagnostics.duplicateMenuIds.push(menu.menuId);
      return;
    }

    menuById[menuId] = menu;
  });

  return menuById;
}

function collectRoleMenus(roleInfo, menuById, diagnostics) {
  const ifMap = roleInfo && roleInfo.ifMap;
  const authorizedById = Object.create(null);

  if (!ifMap || typeof ifMap !== "object") {
    return authorizedById;
  }

  Object.keys(ifMap).forEach(function(key) {
    const roleMenu = ifMap[key] && ifMap[key].menuInfo;
    const menuId = toKey(roleMenu && roleMenu.menuId);

    if (!menuId || !hasOwn(menuById, menuId)) {
      diagnostics.missingAuthorizedMenus.push(
        roleMenu && roleMenu.menuId !== undefined ? roleMenu.menuId : key
      );
      return;
    }

    // 同一个菜单出现多次时，保留第一份权限，避免按钮权限被覆盖。
    if (!hasOwn(authorizedById, menuId)) {
      authorizedById[menuId] = ifMap[key];
    }
  });

  return authorizedById;
}

function includeAncestors(menuId, menuById, includedById, diagnostics) {
  const visited = Object.create(null);
  let currentId = menuId;

  while (currentId && hasOwn(menuById, currentId)) {
    if (visited[currentId]) {
      diagnostics.cycles.push(currentId);
      return;
    }

    visited[currentId] = true;
    includedById[currentId] = true;

    const parentId = getParentKey(menuById[currentId].pid);
    if (!parentId || !hasOwn(menuById, parentId)) {
      return;
    }

    currentId = parentId;
  }
}

function createMenuNode(menu, roleMenu) {
  const icon =
    typeof menu.menuIcon === "string"
      ? menu.menuIcon
      : typeof menu.iconCls === "string"
      ? menu.iconCls
      : typeof menu.icon === "string"
      ? menu.icon
      : "";
  const buttonList = roleMenu && roleMenu.menuBtnList;

  return {
    sort: menu.sort,
    menuId: menu.menuId,
    pid: menu.pid,
    name: typeof menu.menuName === "string" ? menu.menuName : "",
    path: normaliseRoutePath(menu.url),
    icon: icon,
    iconCls: icon,
    // 按钮仅放在被角色直接授权的页面；父级不会继承子级按钮。
    meta: { btns: Array.isArray(buttonList) ? buttonList.slice() : [] },
    children: [],
    isAuthorized: Boolean(roleMenu),
    isContainer: false,
    routeName: "menu-" + String(menu.menuId),
  };
}

function sortTree(nodes) {
  sortNodes(nodes).forEach(function(node) {
    sortTree(node.children);
    node.isContainer = node.children.length > 0;

    if (node.isContainer) {
      node.meta.btns = [];
    }
  });

  return nodes;
}

/**
 * 后端菜单为扁平数组，角色权限为 ifMap。先纳入直接授权菜单及其祖先，
 * 再按 pid 连接为任意深度树；输入对象全程不修改。
 */
export function buildAuthorizedMenuTree(menuList, roleInfo) {
  const diagnostics = {
    invalidMenus: 0,
    duplicateMenuIds: [],
    missingAuthorizedMenus: [],
    cycles: [],
  };
  const menuById = indexMenus(menuList, diagnostics);
  const authorizedById = collectRoleMenus(roleInfo, menuById, diagnostics);
  const includedById = Object.create(null);

  Object.keys(authorizedById).forEach(function(menuId) {
    includeAncestors(menuId, menuById, includedById, diagnostics);
  });

  const nodeById = Object.create(null);
  Object.keys(includedById).forEach(function(menuId) {
    nodeById[menuId] = createMenuNode(menuById[menuId], authorizedById[menuId]);
  });

  const tree = [];
  Object.keys(nodeById).forEach(function(menuId) {
    const node = nodeById[menuId];
    const parentId = getParentKey(node.pid);

    if (parentId && hasOwn(nodeById, parentId)) {
      nodeById[parentId].children.push(node);
    } else {
      tree.push(node);
    }
  });

  return {
    tree: sortTree(tree),
    diagnostics: diagnostics,
  };
}

export default {
  buildAuthorizedMenuTree: buildAuthorizedMenuTree,
};
